<template>
  <breadcrumb-layout
    :org-id="orgId"
    :bread-crumb-items="breadCrumbItems"
    :show-new-goal-button="!team || !team.isArchived"
    :is-archived="initiative && initiative.isArchived"
    :timeline="goal && goal.timeline"
    :team-id="team && team.id"
    @back="onBack"
  >
    <div class="col-12 col-md-11 col-lg-10 initiative-page" v-if="initiative">
      <div class="initiative-content no-gutters card-shadow-primary">
        <initiative-row
          ref="initiative"
          :key="'initiative-' + initiative.id"
          :orgId="orgId"
          :krId="initiative.keyResultId"
          :goalId="initiative.goalId"
          :initiative="initiative"
          :show-details-page-navigator="false"
          :can-toggle-expansion="false"
          :draggable="false"
          :selectable="false"
          @delete-initiative="deleteInitiative"
          @moved-initiative="refreshInitiative"
          @update-initiative="updateInitiative"
          @change-is-closed="onIsClosedChange"
          @awarded="onAwardCreation"
          view-type="details"
        ></initiative-row>
      </div>
      <div class="d-flex justify-content-center">
        <div class="initiative-events col-12 col-lg-10 position-static">
          <feeds-heading
            class="feeds-heading"
            :heading="entityName + ' Activity'"
            :entity-type="ENTITY_TYPE.INITIATIVE"
            :selected-filter="selectedFilter"
          />
          <initiative-discussion
            ref="initiativeEvents"
            :query="$route.query"
            v-if="initiative"
            :key="initiative.id"
            :initiative="initiative"
            :selected-activity-filter="selectedFilter"
          />
        </div>
      </div>
    </div>
  </breadcrumb-layout>
</template>

<script>
import { trackEvent } from '@/analytics'
import BreadcrumbLayout from '@/components/BreadcrumbLayout'
import FeedsHeading from '@/components/FeedsHeading'
import InitiativeRow from '@/components/initiative/InitiativeRow'
import InitiativeDiscussion from '@/components/InitiativeDiscussion'
import { FEED_FILTER_PREFERENCES, FEED_WITH_LOGS } from '@/constants/activity'
import { EVENT_ACTION, EVENT_CATEGORY, EVENT_NAME } from '@/constants/analytics'
import { ENTITY_MAP, ENTITY_TYPE } from '@/constants/entities'
import { featureFlags } from '@/constants/featureFlags'
import { ROUTE_NAMES } from '@/constants/routes'
import EventBus from '@/EventBus'
import EventBusMixin from '@/mixins/EventBusMixin'
import FeaturesMixin from '@/mixins/FeaturesMixin'
import InitiativeDisplayFieldsMixin from '@/mixins/InitiativeDisplayFieldsMixin'
import InitiativesKeyboardNavMixins from '@/mixins/InitiativesKeyboardNavMixins'
import InitiativesMixin from '@/mixins/InitiativesMixin'
import MembershipsMixin from '@/mixins/MembershipsMixin'
import SubOrgIdTrackerMixin from '@/mixins/SubOrgIdTrackerMixin'
import TeamMixin from '@/mixins/TeamMixin'
import * as keymapService from '@/services/keymapService/keymapService'
import getDocumentTitle from '@/utils/getDocumentTitle'
import {
  getGoalPageRouteConfig,
  getKrPageRouteConfig,
  getProjectPageRouteConfig,
  getTeamPageRouteConfigFromTeamInfo
} from '@/utils/navigationConfigs'
import removeQueriesFromRoute from '@/utils/removeQueriesFromRoute'
import URLify from '@/utils/URLify'
import * as R from 'ramda'
import { mapActions, mapGetters } from 'vuex'

/**
 * This is the view for Initiative details page.
 *
 * It can perform some custom actions on load. Action can be defined via query - section
 * section = give-award => launch award modal
 * section = edit-initiative => launch initiative title in edit mode
 * section = edit-note => open note in edit mode
 */
export default {
  name: 'Initiative',
  components: {
    InitiativeDiscussion,
    InitiativeRow,
    BreadcrumbLayout,
    FeedsHeading
  },
  mixins: [
    InitiativesMixin,
    MembershipsMixin,
    EventBusMixin,
    InitiativeDisplayFieldsMixin,
    TeamMixin,
    InitiativesKeyboardNavMixins,
    FeaturesMixin,
    SubOrgIdTrackerMixin
  ],
  props: ['orgId', 'initiativeId'],
  data() {
    return {
      initiative: null
    }
  },
  computed: {
    ...mapGetters('project', ['projectById']),
    ENTITY_TYPE() {
      return ENTITY_TYPE
    },
    entityName() {
      if (!this.initiative) return
      const entityType = this.initiative.parentId ? ENTITY_TYPE.TASK : ENTITY_TYPE.INITIATIVE
      return ENTITY_MAP[entityType].name
    },
    user() {
      return this.$store.getters.user
    },
    goalId() {
      return R.prop(['goalId'], this.initiative)
    },
    goal() {
      return this.$store.getters.goalById(this.goalId)
    },
    project() {
      return this.projectById(this.initiative.projectId)
    },
    teamId() {
      return this.initiative?.teamId
    },
    // This is required to set selected org. It is getting used in SubOrgIdTrackerMixin
    subOrgId() {
      return this.getClosestOrgLikeTeamId(this.teamId)
    },
    team() {
      return this.$store.getters.teamById(this.teamId)
    },
    krId() {
      return R.prop(['keyResultId'], this.initiative)
    },
    kr() {
      return this.$store.getters.keyResultById(this.krId)
    },
    org() {
      return this.$store.getters.orgById(this.goal?.organizationId)
    },
    selectedFilter() {
      return this.$route.query.event
        ? FEED_FILTER_PREFERENCES.find(R.propEq('id', FEED_WITH_LOGS))
        : this.$store.getters['feedPreference/selectedFilter'](ENTITY_TYPE.INITIATIVE)
    },
    breadCrumbItems() {
      if (
        !this.initiative ||
        !this.team ||
        (this.initiative?.goalId && !this.goal) ||
        (this.initiative?.projectId && !this.project)
      )
        return []
      const items = [
        {
          text: this.team.name,
          to: this.team.preferences.enableGoals
            ? getTeamPageRouteConfigFromTeamInfo(this.team)
            : null
        },
        this.initiative.goalId
          ? {
              text: this.goal.title,
              to: getGoalPageRouteConfig({ orgId: this.orgId, goalId: this.goalId }),
              type: ENTITY_TYPE.GOAL
            }
          : {
              text: this.project.title,
              color: this.project.color,
              to: getProjectPageRouteConfig({
                orgId: this.orgId,
                projectId: this.initiative.projectId
              }),
              type: ENTITY_TYPE.PROJECT
            }
      ]
      if (this.kr) {
        items.push({
          text: this.kr.title,
          to: {
            name: ROUTE_NAMES.KR,
            params: { orgId: this.orgId, goalId: this.goalId, krId: this.krId }
          },
          type: ENTITY_TYPE.KEY_RESULT
        })
      }
      if (this.initiative) {
        let item
        if (this.parentId) {
          item = {
            text: this.parent?.title,
            type: ENTITY_TYPE.INITIATIVE,
            to: {
              name: ROUTE_NAMES.INITIATIVE,
              params: { orgId: this.orgId, initiativeId: this.parent?.id }
            }
          }
        } else {
          item = { text: this.initiative.title }
        }
        items.push(item)
      }
      return items
    },
    parentId() {
      return this.initiative?.parentId
    },
    parent() {
      return this.parentId && this.$store.getters.initiativeById(this.parentId)
    },
    historyData() {
      if (this.initiative && this.kr && (!this.parentId || this.parent)) {
        return [
          [
            this.orgId,
            'initiativeId',
            this.initiativeId,
            {
              initiativeTitle: this.initiative.title,
              krTitle: this.kr.title,
              isClosed: this.initiative.isClosed,
              hasParent: !!this.parentId,
              parentTitle: this.parent?.title,
              isParentDoneAndClosed: this.parent?.isDoneAndClosed
            }
          ]
        ]
      }
      return null
    }
  },
  methods: {
    ...mapActions(['getInitiative', 'getGoal']),
    ...mapActions('project', ['getProject']),
    URLify,
    deleteInitiative(initiative) {
      this.$emit('delete-initiative', initiative)
      const location = getKrPageRouteConfig({
        krId: initiative.keyResultId,
        orgId: this.orgId
      })
      this.$router.push(location)
    },
    isTaskSelected() {
      const selectedItem = keymapService.selectedListItem(this.$el)
      return selectedItem?.classList.contains('task-row')
    },
    editInitiative() {
      const initiative = this.getSelectedInitiativeVueComponent()
      if (this.isTaskSelected()) {
        initiative.showActivityModal({ editInitiativeOnLoad: true })
      } else {
        this.$refs.initiative.editable = true
      }
    },
    cancelEditing() {
      this.$refs.initiative.cancelInitiativeEdit()
    },
    async refreshInitiative() {
      this.initiative = await this.getInitiative({
        orgId: this.orgId,
        initiativeId: this.initiativeId,
        includeCustomFields: true,
        includeStandardFields: this.computedFields.join(',')
      })
      if (this.parentId) {
        await this.getInitiative({
          orgId: this.orgId,
          initiativeId: this.parentId,
          includeCustomFields: false
        })
      }
    },
    async loadInitiative() {
      this.initiative = this.$store.getters.initiativeById(this.initiativeId)
      await this.fetchDisplayFields()
      await this.refreshInitiative() // store's initiative might be stale. Better to refresh
    },
    updateHistoryData() {
      EventBus.$emit('UPDATE_RECENT_PAGES_DATA', this.historyData)
    },
    updateInitiative(initiative) {
      this.initiative = initiative
    },
    pushAnalytics() {
      trackEvent(EVENT_CATEGORY.NAVIGATION, EVENT_ACTION.OPEN_PAGE, EVENT_NAME.INITIATIVE)
    },
    onIsClosedChange() {
      EventBus.$emit('INITIATIVE_IS_CLOSED_CHANGED')
    },
    refreshOnComment({ type, value: initiative }) {
      if (type === 'initiative' && initiative.id === this.initiative.id) {
        this.refreshInitiative()
      }
    },
    onAwardCreation() {
      this.$refs.initiativeEvents.refreshEvents()
    },
    handleURLQuery() {
      const {
        query: { section }
      } = this.$route
      if (this.initiative && section) {
        if (section === 'give-award') {
          this.$refs.initiative.showAwardModal()
        } else if (section === 'edit-note') {
          this.$refs.initiative.editNote()
        }
        removeQueriesFromRoute.call(this, ['section'])
      }
    },
    onBack() {
      trackEvent(EVENT_CATEGORY.NAVIGATION, EVENT_ACTION.BACK_BUTTON, EVENT_NAME.INITIATIVE)
    },
    addTask() {
      this.$refs.initiative.$refs.tasks.create()
    },
    enableKBShortcuts() {
      const isTasksFeatureEnabled = this.getFeatureFlagValue(this.orgId, featureFlags.ALLOW_TASKS)
      let handlers = {
        'edit-initiative': this.editInitiative,
        'cancel-editing': this.cancelEditing,
        comment: this.addInitiativeComment
      }
      if (isTasksFeatureEnabled) {
        handlers = {
          ...handlers,
          'previous-item': this.selectPreviousItem,
          'next-item': this.selectNextItem,
          open: this.viewItem,
          'done-undone-item': this.doneUndone,
          'add-task': this.addTask,
          'delete-task': this.triggerDeleteInitiative
        }
      }
      keymapService.activateCategory('initiative', handlers)
    },
    updateDocumentTitle() {
      const title = this.initiative?.title
      if (title) {
        const org = this.getClosestOrgLikeTeam(this.team.id)
        document.title = getDocumentTitle({ title, orgName: org.name })
      }
    },
    fetchInitiativeParentEntity() {
      if (this.initiative.goalId && !this.goal) {
        this.getGoal({ orgId: this.initiative.organizationId, goalId: this.initiative.goalId })
      } else if (this.initiative.projectId && !this.project) {
        this.getProject({
          orgId: this.initiative.organizationId,
          projectId: this.initiative.projectId
        })
      }
    }
  },
  async mounted() {
    try {
      await this.loadInitiative()
    } catch (e) {
      const status = R.path(['response', 'status'], e)
      if (status === 404) {
        EventBus.$emit('SHOW_404')
        return
      }
    }
    this.handleURLQuery()
    this.$nextTick(() => {
      const initiativeRef = this.$refs.initiative
      initiativeRef && (initiativeRef.expanded = true)
    })
    this.enableKBShortcuts()
    this.pushAnalytics()
    this.listenToEventBus('COMMENT_ADDED', this.refreshOnComment)
    this.listenToEventBus('COMMENT_DELETED', this.refreshOnComment)
    this.updateDocumentTitle()
    this.fetchInitiativeParentEntity()
  },
  beforeDestroy() {
    keymapService.deactivateCategory('initiative')
  },
  beforeRouteUpdate(ignoreThis, ignoreThisToo, next) {
    this.pushAnalytics()
    next()
  },
  watch: {
    historyData(newValue, oldValue) {
      if (!oldValue && newValue) {
        this.updateHistoryData()
      }
    },
    async initiativeId() {
      await this.loadInitiative()
      this.$nextTick(() => {
        this.$refs.initiative.expanded = true
      })
      this.historyData && this.updateHistoryData()
      this.handleURLQuery()
      this.updateDocumentTitle()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/styleConfigurations';

.initiative-content {
  padding: 25px 25px 25px 40px;
  border-radius: 10px;
  overflow: visible;
  margin-top: -15px;
  margin-bottom: 15px;
  background: #ffffff;
  font-size: 0.875rem;
}

.initiative-event-list,
.initiative-events {
  margin: 10px 0 30px;
}

.initiative-event-list {
  padding-bottom: 50px;
  padding-top: 10px;
}

.feeds-heading {
  margin-bottom: 25px;
}

@include media-breakpoint-down(md) {
  /deep/ .initiative-content {
    box-shadow: none !important;
    border-radius: 0;
  }
  .initiative-page,
  .initiative-content {
    padding: 0;
  }
  /deep/ .initiative-row {
    border-top: none;

    .summary {
      margin: 0;
    }
  }
  .initiative-event-list,
  .initiative-events {
    padding-left: 4px;
    margin-top: 8px !important;
    margin-bottom: 0;

    h2 {
      margin-bottom: 24px;
    }
  }
}
</style>
